import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { listenToHostReservations } from "../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../redux/authSlice";
import { listenToShortstayData } from "../../../redux/shortstaysSlice";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const ShortReservations: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.userId || user?.uid || null;
  const authLoaded = useSelector((state: RootState) => state.auth.authLoaded);

  const reservations = useSelector(
    (state: RootState) => state.shortstayReservations.hostReservations
  );
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);
  const listings = useSelector((state: RootState) => state.shortstays.listings);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [fetchedListings, setFetchedListings] = useState<string[]>([]);

  useEffect(() => {
    if (authLoaded && userId) {
      dispatch(listenToHostReservations(userId));
      dispatch(fetchAllUsers());
    }
  }, [authLoaded, userId, dispatch]);

  useEffect(() => {
    reservations.forEach((res) => {
      if (!fetchedListings.includes(res.listingId)) {
        dispatch(listenToShortstayData(res.listingId));
        setFetchedListings((prev) => [...prev, res.listingId]);
      }
    });
  }, [reservations, fetchedListings, dispatch]);

  const getGuestProfile = (guestId: string) =>
    allUsers.find((u) => u.userId === guestId);

  const getListingPhoto = (listingId: string) => {
    const listing = listings.find((l) => l.listingId === listingId);
    return listing?.photos?.[0] || "/fallback.jpg";
  };

  const getListingAddress = (listingId: string) => {
    const listing = listings.find((l) => l.listingId === listingId);
    const address = listing?.address || {};
    const parts = [address.street, address.city, address.country].filter(Boolean);
    return parts.join(", ");
  };

  // ── matches ShortRequests web pattern: /aboutme/@username ──
  const handleGuestClick = (guest: any, guestId: string) => {
    navigate(`/aboutme/@${guest?.username || guestId}`);
  };

  const handleToggleExpand = (id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  const activeReservations = reservations.filter(
    (r) => !r.isCancelled && !r.isCompleted && !r.isCheckingOut
  );

  const getActiveFlagLabel = (r: any) => {
    if (r.isCheckingIn)   return "Checking In";
    if (r.isOngoing)      return "Ongoing";
    if (r.isCheckingOut)  return "Checking Out";
    if (r.isArrivingSoon) return "Arriving Soon";
    if (r.isUpcoming)     return "Upcoming";
    if (r.isHosted)       return "Hosted";
    return "Active";
  };

  return (
    <div style={styles.gridContainer}>
      {activeReservations.length === 0 ? (
        <p style={{ textAlign: "center", opacity: 0.6 }}>No active reservations</p>
      ) : (
        activeReservations.map((res) => {
          const guest        = getGuestProfile(res.guestId);
          const listingPhoto = getListingPhoto(res.listingId);
          const flag         = getActiveFlagLabel(res);
          const initials     = guest?.firstName?.[0]?.toUpperCase() || "G";
          const disableCancel = res.hasCheckedIn || res.isOngoing || res.isCheckingOut;

          return (
            <div key={res.id} style={styles.card}>
              <div style={styles.cardHeader} onClick={() => handleToggleExpand(res.id)}>
                <img src={listingPhoto} style={styles.thumbnail} alt="listing" />

                <div style={styles.headerInfo}>
                  <div style={styles.guestSection}>
                    {guest?.profilePicture ? (
                      <img
                        src={guest.profilePicture}
                        style={styles.avatar}
                        alt="guest"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGuestClick(guest, res.guestId);
                        }}
                      />
                    ) : (
                      <div
                        style={styles.initialsCircle}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGuestClick(guest, res.guestId);
                        }}
                      >
                        {initials}
                      </div>
                    )}

                    <div>
                      <p style={styles.guestName}>
                        {guest?.firstName || "Guest"} {guest?.lastName || ""}
                      </p>
                      <p style={styles.flagLabel}>{flag}</p>
                    </div>
                  </div>
                </div>

                <div style={styles.chevron}>
                  {expanded === res.id ? <ChevronUp /> : <ChevronDown />}
                </div>
              </div>

              {expanded === res.id && (
                <div style={styles.expand}>
                  <p><strong>Check-In:</strong> {new Date(res.checkIn).toLocaleDateString()}</p>
                  <p><strong>Check-Out:</strong> {new Date(res.checkOut).toLocaleDateString()}</p>

                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
                    {res.hasCheckedIn ? (
                      <CheckCircleIcon style={{ color: "#4caf50", fontSize: 18 }} />
                    ) : (
                      <CancelIcon style={{ color: "#d32f2f", fontSize: 18 }} />
                    )}
                    <span style={styles.checkInLabel}>Checked In</span>
                  </div>

                  <p><strong>Location:</strong> {getListingAddress(res.listingId)}</p>
                  <p><strong>Total Paid:</strong> €{res.totalPaid?.toFixed(2)}</p>
                  <p><strong>Host Service Fee:</strong> €{res.hostsFee?.toFixed(2)}</p>
                  <p><strong>Estimated Profit:</strong> €{res.hostProfit?.toFixed(2)}</p>
                  <p><strong>Reserved:</strong> {res.reservedDates} nights</p>

                  {/* View guest profile — matches ShortRequests pattern */}
                  <button
                    style={styles.viewProfileBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGuestClick(guest, res.guestId);
                    }}
                  >
                    View {guest?.firstName || "guest"}'s profile
                  </button>

                  <button
                    style={{
                      ...styles.cancelBtn,
                      ...(disableCancel ? {
                        background: "#ccc",
                        color: "#666",
                        cursor: "not-allowed",
                        opacity: 0.7,
                        boxShadow: "none",
                      } : {}),
                    }}
                    disabled={disableCancel}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disableCancel) return;
                      navigate(
                        `/hostcancelshortbooking/${userId}/${res.listingId}/${res.id}`,
                        { state: { booking: res } }
                      );
                    }}
                  >
                    Cancel Booking
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default ShortReservations;

const styles: { [key: string]: React.CSSProperties } = {
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
    padding: "10px",
    justifyContent: "center",
    alignItems: "start",
  },
  card: {
    backgroundColor: "rgba(255,255,255,0.8)",
    backdropFilter: "blur(6px)",
    borderRadius: "16px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
    width: "100%",
    maxWidth: "600px",
    margin: "0 auto",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
  },
  thumbnail: {
    width: "90px",
    height: "90px",
    borderRadius: "12px",
    objectFit: "cover",
    marginRight: "16px",
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
  },
  headerInfo: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
  },
  guestSection: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  avatar: {
    width: "45px",
    height: "45px",
    borderRadius: "50%",
    objectFit: "cover",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    cursor: "pointer",
  },
  initialsCircle: {
    width: "45px",
    height: "45px",
    borderRadius: "50%",
    background: "linear-gradient(135deg,#111,#444)",
    color: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: 700,
    fontSize: "16px",
    cursor: "pointer",
  },
  guestName:     { fontWeight: 600, marginBottom: "2px" },
  flagLabel:     { fontSize: "13px", color: "#555", opacity: 0.9 },
  checkInLabel:  { fontSize: "12px", color: "#555" },
  chevron:       { marginLeft: "12px", opacity: 0.7, display: "flex", alignItems: "center" },
  expand: {
    padding: "12px 18px",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderBottomLeftRadius: "16px",
    borderBottomRightRadius: "16px",
    fontSize: "14px",
    color: "#222",
  },
  viewProfileBtn: {
    marginTop: "10px",
    marginBottom: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: "8px",
    border: "1px solid #ccc",
    background: "#fff",
    color: "#222",
    cursor: "pointer",
    width: "100%",
  },
  cancelBtn: {
    marginTop: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: "0px",
    border: "none",
    background: "linear-gradient(145deg, #cc0000, #e20f0fff)",
    color: "#fff",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
    transition: "all 0.2s ease",
    width: "fit-content",
  },
};